import { f7 } from "framework7-vue";
import { openAdaptiveModal, closeAdaptiveModal } from "./useAdaptiveModal.js";
import { Capacitor } from "@capacitor/core";
import {
  Camera,
  CameraDirection,
  CameraResultType,
  CameraSource,
} from "@capacitor/camera";
import { CameraPreview } from "@capgo/camera-preview";
import { useAuthStore } from "../pinia-stores/auth.js";
import { useCameraStore } from "../pinia-stores/camera.js";
import { utils } from "framework7";
import { storeToRefs } from "pinia";

const RECORDING_POPUP_SELECTOR = ".recordVideoTakePicture";

export function useCamera({ setAttachment } = {}) {
  const cameraStore = useCameraStore();
  const {
    videoElement,
    mediaStream,
    currentMediaRecorder,
    isCameraOpen,
    isMediaRecording,
    cameras,
    cameraIndex,
    cameraId,
    flashModes,
    flashMode,
    currentFlashModeIndex,
    torchSupported,
    torchActive,
    front,
  } = storeToRefs(cameraStore);

  // --- Helpers ---
  function getRecordingMimeType() {
    if (typeof MediaRecorder === "undefined") return "";
    return ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
      .find((type) => MediaRecorder.isTypeSupported(type)) || "";
  }

  function extensionFor(mimeType) {
    return mimeType.split("/")[1]?.split(";")[0] || "webm";
  }

  // --- Core Media Handling ---
  async function setCameraAttachment({ file, previewUrl }) {
    if (!file) return;

    // Create preview data to display before upload
    const mediaData = {
      fileName: file.name,
      contentType: file.type,
      fileType: file.type.split("/")[0],
      url: previewUrl || window.URL.createObjectURL(file),
      file,
    };

    if (setAttachment) {
      setAttachment(mediaData);
      return;
    }

    // Default: Add to store
    useAuthStore().setDocumentsUrl({
      ...mediaData,
      fileId: `${utils.id("xxxx-xxxx-xxxx-xxxx")}/${file.name}`,
      size: file.size,
    });
  }

  // --- Device enumeration & switching ---
  async function getDevices() {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices();
    cameras.value = devices.filter((device) => device.kind === "videoinput");
    front.value = cameras.value.length === 1;
  }

  async function switchCamera() {
    if (Capacitor.isNativePlatform()) {
      try {
        await CameraPreview.flip();
      } catch (error) {
        console.error("Failed to flip native camera:", error);
      }
      return;
    }
    await switchWebCamera();
  }

  async function switchWebCamera() {
    if (!cameras.value || cameras.value.length === 0) await getDevices();
    if (!cameras.value || cameras.value.length === 0) return;
    if (!mediaStream.value) return;
    cameraId.value = cameras.value[cameraIndex.value].deviceId;
    cameraIndex.value =
      cameraIndex.value + 1 < cameras.value.length ? cameraIndex.value + 1 : 0;
    mediaStream.value.getTracks().forEach((track) => track.stop());
    await new Promise((resolve) => setTimeout(resolve, 100));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: cameraId.value } },
        audio: { echoCancellation: true },
      });
      const element = videoElement.value;
      if (element) {
        element.srcObject = stream;
        element.play().catch(() => {});
      }
      mediaStream.value = stream;
    } catch (error) {
      console.error("Failed to switch camera:", error);
    }
  }

  // --- Flash / torch ---
  async function setFlashMode() {
    if (!Capacitor.isNativePlatform()) return;
    if (!flashModes.value || flashModes.value.length === 0) {
      try {
        const result = await CameraPreview.getSupportedFlashModes();
        flashModes.value = result.result || [];
      } catch (error) {
        console.error("Failed to get flash modes:", error);
        return;
      }
    }
    if (!(flashModes.value && flashModes.value.length > 0)) return;
    const fm =
      flashModes.value[currentFlashModeIndex.value % flashModes.value.length];
    flashMode.value = fm;
    try {
      await CameraPreview.setFlashMode({ flashMode: fm });
    } catch (error) {
      console.error("Failed to set flash mode:", error);
    }
    currentFlashModeIndex.value =
      (currentFlashModeIndex.value + 1) % flashModes.value.length;
  }

  async function toggleTorch() {
    const track = mediaStream.value?.getVideoTracks()[0];
    if (!track) return;
    torchActive.value = !torchActive.value;
    try {
      await track.applyConstraints({
        advanced: [{ torch: torchActive.value }],
      });
    } catch (error) {
      console.error("Torch not supported:", error);
      torchActive.value = !torchActive.value;
    }
  }

  function updateFlashAvailability() {
    const track = mediaStream.value?.getVideoTracks()[0];
    const element = videoElement.value;
    if (!track || !element) return;
    element.addEventListener("loadedmetadata", () => {
      try {
        const capabilities = track.getCapabilities
          ? track.getCapabilities()
          : {};
        if (
          capabilities.torch ||
          track.getSettings()?.torch ||
          (capabilities.fillLightMode &&
            capabilities.fillLightMode !== "none")
        ) {
          torchSupported.value = true;
        }
      } catch (error) {
        console.error("Failed to detect torch support:", error);
      }
    });
  }

  // --- Browser Recording/Capture ---
  async function startWebVideoCamera() {
    if (!navigator.mediaDevices?.getUserMedia)
      throw new Error("Camera not supported.");

    await getDevices();
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        facingMode: f7.device.desktop ? "user" : "environment",
      },
      audio: { echoCancellation: true },
    });

    attachStream(stream);
  }

  function attachStream(stream) {
    let element = videoElement.value;
    if (!element) {
      element = document.getElementById("videoRecorder");
      if (element) cameraStore.setVideoElement(element);
    }
    if (!element) {
      stream.getTracks().forEach((track) => track.stop());
      throw new Error("View unavailable.");
    }
    element.srcObject = stream;
    element.play().catch(() => {});
    mediaStream.value = stream;
    isCameraOpen.value = true;
    updateFlashAvailability();
  }

  async function recordWebVideo() {
    const stream = mediaStream.value;
    if (!stream) throw new Error("Camera stream not ready.");
    if (typeof MediaRecorder === "undefined")
      throw new Error("Video recording not supported.");

    const chunks = [];
    const mimeType = getRecordingMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    currentMediaRecorder.value = recorder;
    isMediaRecording.value = true;

    recorder.ondataavailable = ({ data }) => {
      if (data.size > 0) chunks.push(data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || "video/webm";
      if (chunks.length) {
        const file = new File(
          [new Blob(chunks, { type })],
          `video_${Date.now()}.${extensionFor(type)}`,
          { type }
        );
        setCameraAttachment({ file });
      }
      currentMediaRecorder.value = null;
      isMediaRecording.value = false;
    };
    recorder.start();
  }

  async function capTureWebPhoto() {
    const video = videoElement.value;
    if (!video?.videoWidth || !video?.videoHeight)
      throw new Error("Camera preview not ready.");

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/webp", 0.9)
    );
    if (!blob) throw new Error("Capture failed.");

    setCameraAttachment({
      file: new File([blob], `image_${Date.now()}.webp`, { type: "image/webp" }),
    });
    stopWebCamera();
    closeAdaptiveModal(RECORDING_POPUP_SELECTOR, true);
  }

  function stopWebCamera() {
    if (
      currentMediaRecorder.value &&
      currentMediaRecorder.value.state !== "inactive"
    ) {
      currentMediaRecorder.value.stop();
    }
    mediaStream.value?.getTracks().forEach((track) => track.stop());
    if (videoElement.value) {
      videoElement.value.pause();
      videoElement.value.srcObject = null;
    }
    cameraStore.resetState();
  }

  // --- Capacitor/Native ---
  async function takePhotoNative() {
    await takePhoto();
  }

  async function takePhoto() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        source: CameraSource.Camera,
        direction: f7.device.desktop
          ? CameraDirection.Front
          : CameraDirection.Rear,
        resultType: CameraResultType.Uri,
      });
      await convertNativeImageToFile(image);
    } catch (error) {
      if (error && /cancel/i.test(error.message || "")) return;
      console.error("takePhoto failed:", error);
    }
  }

  async function convertNativeImageToFile(image) {
    const mimeType = "image/jpeg";
    let blob = null;
    if (image.webPath) {
      blob = await (await fetch(image.webPath)).blob();
    } else if (image.path) {
      const source =
        image.path.startsWith("http") ||
        image.path.startsWith("capacitor") ||
        image.path.startsWith("app")
          ? image.path
          : Capacitor.convertFileSrc(image.path);
      blob = await (await fetch(source)).blob();
    } else if (image.dataUrl) {
      blob = await (await fetch(image.dataUrl)).blob();
    }
    if (!blob) return;
    const type = blob.type || mimeType;
    setCameraAttachment({
      file: new File([blob], `photo_${Date.now()}.${extensionFor(type)}`, {
        type,
      }),
    });
  }

  async function convertVideoFileToAttachment(filePath) {
    if (!filePath) return;
    const source = /^https?:/.test(filePath)
      ? filePath
      : Capacitor.convertFileSrc(filePath);
    const blob = await (await fetch(source)).blob();
    const mimeType = blob.type || "video/mp4";
    setCameraAttachment({
      file: new File(
        [blob],
        `video_${Date.now()}.${extensionFor(mimeType)}`,
        { type: mimeType }
      ),
    });
  }

  async function recordVideoNative() {
    try {
      const result = await Camera.recordVideo({
        saveToGallery: false,
        isPersistent: false,
        includeMetadata: true,
      });
      if (result?.uri) {
        await convertVideoFileToAttachment(result.uri);
      }
      return result || null;
    } catch (error) {
      if (error && /cancel/i.test(error.message || "")) return null;
      console.error("recordVideoNative failed:", error);
      throw error;
    }
  }

  async function startNativePreview() {
    if (isCameraOpen.value) return;
    const width = Math.floor(
      window.innerWidth || document.documentElement.clientWidth
    );
    const height = Math.floor(
      window.innerHeight || document.documentElement.clientHeight
    );
    await CameraPreview.start({
      position: front.value ? "front" : "rear",
      width,
      height,
      toBack: true,
      enableVideoMode: true,
      disableAudio: false,
      videoQuality: "high",
    });
    isCameraOpen.value = true;
  }

  async function stopNativePreview() {
    try {
      await CameraPreview.stop();
    } catch (error) {
      console.error("Failed to stop camera preview:", error);
    }
    isCameraOpen.value = false;
    isMediaRecording.value = false;
  }

  // --- Exposed Actions ---
  async function openPreviewCamera() {
    openAdaptiveModal(RECORDING_POPUP_SELECTOR, true);
    await new Promise((resolve) => requestAnimationFrame(resolve));
    try {
      if (Capacitor.isNativePlatform()) {
        await startNativePreview();
      } else {
        await startWebVideoCamera();
      }
    } catch (error) {
      console.error(error);
      closeAdaptiveModal(RECORDING_POPUP_SELECTOR);
    }
  }

  async function startPreviewRecording() {
    if (isMediaRecording.value) return;
    if (Capacitor.isNativePlatform()) {
      await stopNativePreview();
      closeAdaptiveModal(RECORDING_POPUP_SELECTOR);
      await recordVideoNative();
      return;
    }
    if (!mediaStream.value) await startWebVideoCamera();
    await recordWebVideo();
  }

  async function stopPreviewRecording() {
    if (Capacitor.isNativePlatform()) {
      await stopNativePreview();
      closeAdaptiveModal(RECORDING_POPUP_SELECTOR);
      return;
    }
    stopWebCamera();
    closeAdaptiveModal(RECORDING_POPUP_SELECTOR);
  }

  async function cameraCapture() {
    if (!Capacitor.isNativePlatform()) {
      await capTureWebPhoto();
      return;
    }
    const result = await CameraPreview.capture({});
    const fileName = `image_${new Date().getTime()}.webp`;
    const mimeType = "image/webp";
    const response = await fetch(`data:${mimeType};base64,${result.value}`);
    const blob = await response.blob();
    setCameraAttachment({
      file: new File([blob], fileName, { type: mimeType }),
    });
  }

  async function capturePhoto() {
    if (!Capacitor.isNativePlatform()) await capTureWebPhoto();
    else await takePhotoNative();
  }

  return {
    // Refs
    videoElement,
    mediaStream,
    currentMediaRecorder,
    isCameraOpen,
    isMediaRecording,
    cameras,
    cameraIndex,
    cameraId,
    flashModes,
    flashMode,
    currentFlashModeIndex,
    torchSupported,
    torchActive,
    front,
    // Actions
    openPreviewCamera,
    startPreviewRecording,
    stopPreviewRecording,
    cameraCapture,
    capturePhoto,
    takePhoto,
    recordVideoNative,
    switchCamera,
    setFlashMode,
    toggleTorch,
    // Internals (kept for compatibility)
    startWebVideoCamera,
    recordWebVideo,
    capTureWebPhoto,
    stopWebCamera,
    getDevices,
    updateFlashAvailability,
    switchWebCamera,
    convertNativeImageToFile,
    convertVideoFileToAttachment,
    startNativePreview,
    stopNativePreview,
    takePhotoNative,
    setCameraAttachment,
  };
}
